import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import * as api from "../../lib/api.ts";
import { useSession } from "../../lib/session.tsx";
import { redeemedMe } from "../join/redeemed-me.ts";
import { afterLessonProbe, linkFailure } from "./lesson-link-gate-rules.ts";

/**
 * Вход в урок по ссылке ученика на одно занятие: `/lesson/<id>?k=<ключ>`.
 *
 * ⚠️ **Этот файл едет в СТАРТОВОМ чанке**, потому что его зовёт `LessonPage`, а
 * она стартовая (разбор в её шапке). Отсюда правило: ни одного импорта из
 * комнаты урока. Только API, сессия, помощник сборки сессии и чистые правила.
 *
 * ⚠️ **Первое, что делает шлюз, это убирает `k` из адреса, до единого
 * запроса.** `Referrer-Policy same-origin` кладёт адрес страницы в Referer
 * каждого запроса с неё, включая `/v1/*`, и ключ уезжал бы в каждый из них.
 * Ключ приходит сюда из памяти `LessonPage`, и дальше, включая экран отказа и
 * повтор, работаем только с ним. Замена, а не переход: в истории общего
 * компьютера ключу тоже не место. Остальные параметры (`relay`, `debug`)
 * сохраняются.
 *
 * Исходы, и каждый кончается либо входом, либо экраном:
 *
 * - **аноним**: гасим ключ, принимаем сессию ученика так же, как экран
 *   приглашения, и входим;
 * - **сессия есть и урок ей доступен** (сам ученик или сам преподаватель,
 *   проверяющий ссылку): просто входим, ключ не гасится вовсе;
 * - **сессия чужая или протухла** (урок отвечает 404 или 401): гасим ключ, и
 *   сервер либо впускает (200), либо отвечает 409, и показывается выбор, как
 *   на экране приглашения;
 * - **отказ**: текст по коду (`linkFailure`).
 */
export function LessonLinkGate(props: {
  lessonId: string;
  linkKey: string;
  /** Вход состоялся: страница урока забывает ключ и открывает комнату. */
  onEntered: () => void;
}) {
  const { lessonId, linkKey, onEntered } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { state, adopt, signOut } = useSession();

  const [conflict, setConflict] = useState<api.AlreadySignedIn | null>(null);
  const [failed, setFailed] = useState<ReturnType<typeof linkFailure> | null>(null);
  /**
   * Идёт действие. Гасит обе кнопки выбора: двойное нажатие «Выйти и войти»
   * выходило бы дважды и гасило ключ дважды наперегонки.
   */
  const [busy, setBusy] = useState(true);
  /** Что повторяет «Повторить»: погашение или смену входа, смотря что упало. */
  const retryWhat = useRef<"redeem" | "switch">("redeem");
  // StrictMode в деве монтирует эффект дважды, а погашение заводит сессию
  const started = useRef(false);
  const stripped = useRef(false);

  /** Убрать `k` из адреса. Первый эффект шлюза: он идёт раньше запросов. */
  useEffect(() => {
    if (stripped.current) return;
    stripped.current = true;
    const rest = new URLSearchParams(location.search);
    if (!rest.has("k")) return;
    rest.delete("k");
    const search = rest.toString();
    void navigate(
      { pathname: location.pathname, search: search === "" ? "" : `?${search}`, hash: location.hash },
      { replace: true },
    );
  }, [navigate, location.pathname, location.search, location.hash]);

  const fail = useCallback((code: string | "signout" | null, what: "redeem" | "switch") => {
    retryWhat.current = what;
    setConflict(null);
    setFailed(linkFailure(code));
  }, []);

  const redeem = useCallback(async () => {
    setBusy(true);
    setFailed(null);
    setConflict(null);
    try {
      const res = await api.redeemLessonLink(lessonId, linkKey);
      if (res.kind === "conflict") {
        setConflict(res.info);
        return;
      }
      adopt(redeemedMe(res.session));
      onEntered();
    } catch (e) {
      fail(e instanceof api.ApiError ? e.code : null, "redeem");
    } finally {
      setBusy(false);
    }
  }, [lessonId, linkKey, adopt, onEntered, fail]);

  /** «Выйти и войти как ученик»: выход из чужой сессии и погашение ключа. */
  const switchUser = useCallback(async () => {
    setBusy(true);
    try {
      await signOut();
    } catch {
      /*
        ⚠️ Не молчим: `signOut` на этом устройстве уже показал экран анонима,
        но cookie на сервере могла остаться живой, и погашение ответило бы тем
        же 409. Человек увидел бы тот же выбор без объяснения.
      */
      fail("signout", "switch");
      setBusy(false);
      return;
    }
    await redeem();
  }, [signOut, redeem, fail]);

  useEffect(() => {
    if (state.status === "loading" || started.current) return;
    started.current = true;
    if (state.status === "anon") {
      void redeem();
      return;
    }
    /**
     * Сессия уже есть. Сначала спрашиваем урок её же правами: преподаватель,
     * открывший свою ссылку проверить, и ученик, у которого сессия уже есть,
     * входят без погашения. Что делать с ответом, решает `afterLessonProbe`.
     */
    void (async () => {
      let result: "ok" | { status: number | null } = "ok";
      try {
        await api.getLesson(lessonId);
      } catch (e) {
        result = { status: e instanceof api.ApiError ? e.status : null };
      }
      if (afterLessonProbe(result) === "redeem") {
        await redeem();
        return;
      }
      onEntered();
    })();
  }, [state.status, lessonId, redeem, onEntered]);

  if (conflict) {
    return (
      <main className="lobby">
        <h1>Вы уже вошли</h1>
        <p>
          Эта ссылка ведёт на занятие ученика «{conflict.studentDisplayName}» у преподавателя «
          {conflict.teacherDisplayName}», а в этом браузере открыт другой вход.
        </p>
        <div className="lobby__row">
          <button className="btn" disabled={busy} onClick={() => void switchUser()}>
            Выйти и войти как ученик
          </button>
          {/*
            ⚠️ Акцента нет намеренно, по той же причине, что на экране
            приглашения (`JoinPage`): ссылку часто открывает сам преподаватель
            из своей вкладки, и для него верный ответ это «Остаться». Заливка
            на «Выйти и войти» выбивала бы его из собственной сессии одним
            привычным нажатием.
          */}
          <button
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => void navigate("/", { replace: true })}
          >
            Остаться
          </button>
        </div>
      </main>
    );
  }

  if (failed !== null) {
    return (
      <main className="lobby">
        <h1>Ссылка не сработала</h1>
        {failed.text.map((line) => (
          <p key={line} className="err">
            {line}
          </p>
        ))}
        {failed.retry && (
          <div className="lobby__row">
            {/* Тихая, как в `JoinPage`: главное на экране это текст */}
            <button
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => void (retryWhat.current === "switch" ? switchUser() : redeem())}
            >
              Повторить
            </button>
          </div>
        )}
      </main>
    );
  }

  return (
    <main className="lobby">
      <h1>Урок</h1>
      <p className="muted">Проверяем ссылку…</p>
    </main>
  );
}
